import { createContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { setAccessToken, clearTokens, getAccessToken } from '@utils/token/token.storage';
import { isTokenValid, getTokenExpirationTime, getUserFromToken } from '@utils/token/token.service';
import { AuthUser } from 'types/entities';
import { useMsal } from '@azure/msal-react';
import { InitiativeSupport } from 'types/entities/initiativeSupport';

export interface AuthContextType {
  isAuthenticated: boolean;
  user: AuthUser | null;
  token: string | null;
  userInitiativeSupports: InitiativeSupport[];
  updateInitiativeSupports: (initiativeSupports: InitiativeSupport[]) => void;
  login: (accessToken: string, rememberMe?: boolean) => void;
  logout: () => void;
  logoutWithoutRedirect: () => void;
  refreshAuth: () => void;
}

export const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  user: null,
  token: null,
  userInitiativeSupports: [],
  updateInitiativeSupports: () => undefined,
  login: () => undefined,
  logout: () => undefined,
  logoutWithoutRedirect: () => undefined,
  refreshAuth: () => undefined,
});

interface AuthProviderProps {
  children: ReactNode;
}

type AuthState = {
  token: string | null;
  user: AuthUser | null;
  isAuthenticated: boolean;
};

function readAuthFromStorage(): AuthState {
  const storedToken = getAccessToken();

  if (storedToken && isTokenValid(storedToken)) {
    return {
      token: storedToken,
      isAuthenticated: true,
      user: getUserFromToken(),
    };
  }

  if (storedToken) {
    clearTokens();
  }

  return { token: null, isAuthenticated: false, user: null };
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [auth, setAuth] = useState<AuthState>(() => readAuthFromStorage());
  const [userInitiativeSupports, setUserInitiativeSupports] = useState<InitiativeSupport[]>([]);
  const { instance } = useMsal();

  const refreshAuth = useCallback(() => {
    const nextAuth = readAuthFromStorage();
    setAuth(nextAuth);
  }, []);

  const login = useCallback(
    (accessToken: string, rememberMe?: boolean) => {
      setAccessToken(accessToken, { rememberMe: rememberMe ?? true });
      refreshAuth();
    },
    [refreshAuth]
  );

  const logout = useCallback(() => {
    clearTokens();
    setAuth({ token: null, isAuthenticated: false, user: null });
    instance.setActiveAccount(null);
    void instance
      .clearCache()
      .catch(() => undefined)
      .then(() => {
        window.location.replace(`${window.location.origin}/`);
      });
  }, [instance]);

  const logoutWithoutRedirect = useCallback(() => {
    clearTokens();
    // setAuth({ token: null, isAuthenticated: false, user: null });
    instance.setActiveAccount(null);
    void instance.clearCache().catch(() => undefined);
  }, [instance]);

  const updateInitiativeSupports = useCallback((initiativeSupports: InitiativeSupport[]) => {
    setUserInitiativeSupports(initiativeSupports);
  }, []);

  useEffect(() => {
    refreshAuth();
  }, [refreshAuth]);

  useEffect(() => {
    if (!auth.isAuthenticated) return;

    const checkTokenExpiration = () => {
      const expirationTime = getTokenExpirationTime();

      if (expirationTime === null || expirationTime <= 0) {
        logout();
        return;
      }

      const interval = setInterval(() => {
        refreshAuth();
      }, 60000);

      return () => clearInterval(interval);
    };

    const cleanup = checkTokenExpiration();

    return () => {
      if (cleanup) cleanup();
    };
  }, [auth.isAuthenticated, logout, refreshAuth]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: auth.isAuthenticated,
        user: auth.user,
        token: auth.token,
        userInitiativeSupports,
        updateInitiativeSupports,
        login,
        logout,
        logoutWithoutRedirect,
        refreshAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
